import { EVENTS, simulationDeepDiveEventBus } from "@/inspection-events"
import type { AudioRegionReviewConfig } from "@/metrics/runtime"

import { logger } from "@/lib/logger"
import type { AudioRegionParams } from "@/components/audio-player/AudioPlayer"
import type {
  MetricOutputDisplaySource,
  RunMetricOutput,
  SubvalueByTimestamp,
} from "@/types/RunMetricOutput"
import { cn } from "@/utils/cn"

import { NumericalMetricRecord } from "../base/NumericalMetricRecord"
import { getStat } from "../helpers/getStat"
import { parseResultJson } from "../helpers/parseResultJson"
import { WithAudioFilterMixin } from "../mixins/WithAudioFilterMixin"

const log = logger("InterruptionRateMetric")

/** Convert seconds → m:ss (no hours) */
const fmt = (sec: number) => {
  const m = Math.floor(sec / 60)
    .toString()
    .padStart(1, "0")
  const s = Math.floor(sec % 60)
    .toString()
    .padStart(2, "0")
  return `${m}:${s}`
}

const CATEGORY = "interruption"

interface InterruptionRateResultJson {
  total_interruptions?: number
  interruptions_per_minute?: number
  subvalues_by_timestamp?: SubvalueByTimestamp[]
}

interface InterruptionDurationSummary {
  mean?: number
  median?: number
}

export class InterruptionRateMetricRecord extends WithAudioFilterMixin(
  NumericalMetricRecord,
) {
  overviewSize = "small" as const
  unit = "/min"
  aggregate_units = "/min"

  _renderDeepDive(mo: RunMetricOutput): React.ReactNode {
    const data =
      parseResultJson<InterruptionRateResultJson>(mo) ??
      ({} as InterruptionRateResultJson)
    // Prefer typed result fields; fall back to legacy result_json keys.
    const total_interruptions =
      (mo.result?.count as number | undefined) ?? data.total_interruptions ?? 0
    const interruptions_per_minute =
      (mo.result?.frequency as number | undefined) ??
      data.interruptions_per_minute ??
      0

    const durationSummary = getStat<InterruptionDurationSummary>(
      mo,
      "summary",
      "interruption_duration_seconds",
    )
    const meanDuration = durationSummary?.mean
    const medianDuration = durationSummary?.median

    const subvalues: SubvalueByTimestamp[] =
      mo.subvalues_by_timestamp?.length > 0
        ? mo.subvalues_by_timestamp
        : Array.isArray(data.subvalues_by_timestamp)
          ? data.subvalues_by_timestamp
          : []
    const interruptions: { start: number; end: number }[] = subvalues.map(
      (item) => ({ start: item.start_offset, end: item.end_offset }),
    )

    const handleSeekToTimestamp = (timestamp: number) => {
      simulationDeepDiveEventBus.dispatch(EVENTS.AUDIO_SEEK, {
        time: timestamp,
      })
    }

    return (
      <div className="space-y-6">
        <div className="grid grid-cols-2 gap-4">
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
            <div className="text-sm text-amber-600 font-medium mb-1">
              Interruptions / Minute
            </div>
            <div className="text-2xl font-bold text-amber-800">
              {interruptions_per_minute.toFixed(2)}
            </div>
          </div>

          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
            <div className="text-sm text-amber-600 font-medium mb-1">
              Total Interruptions
            </div>
            <div className="text-2xl font-bold text-amber-800">
              {total_interruptions.toFixed(0)}
            </div>
          </div>

          {meanDuration != null && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
              <div className="text-sm text-amber-600 font-medium mb-1">
                Mean Duration
              </div>
              <div className="text-2xl font-bold text-amber-800">
                {meanDuration.toFixed(2)} s
              </div>
            </div>
          )}

          {medianDuration != null && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
              <div className="text-sm text-amber-600 font-medium mb-1">
                Median Duration
              </div>
              <div className="text-2xl font-bold text-amber-800">
                {medianDuration.toFixed(2)} s
              </div>
            </div>
          )}
        </div>

        {interruptions.length > 0 && (
          <div className="space-y-3">
            <h3 className="text-sm font-semibold text-coval-text">
              Interruptions ({interruptions.length})
            </h3>
            <div className="space-y-2">
              {interruptions.map(({ start, end }, idx) => (
                <button
                  type="button"
                  key={idx}
                  className={cn(
                    "flex w-full items-start gap-3 p-3 rounded-lg border border-transparent text-left",
                    "bg-surface-2 text-coval-text",
                    "cursor-pointer transition-colors duration-150",
                  )}
                  onClick={() => handleSeekToTimestamp(start)}
                >
                  <span className="shrink-0">
                    <span
                      className={cn(
                        "block px-2 py-1 rounded text-xs font-mono",
                        "bg-surface-2 text-coval-text",
                      )}
                    >
                      {fmt(start)} - {fmt(end)}
                    </span>
                  </span>
                  <span className="block flex-1 min-w-0">
                    <span className="text-xs font-medium">
                      Interruption {idx + 1}
                    </span>
                  </span>
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    )
  }

  getAudioRegions(mo: MetricOutputDisplaySource): AudioRegionParams[] {
    if (mo?.error_message && mo?.error_message !== "") {
      return []
    }
    try {
      const legacy = parseResultJson<InterruptionRateResultJson>(mo)
      const subvalues: SubvalueByTimestamp[] =
        (mo.subvalues_by_timestamp?.length ?? 0) > 0
          ? (mo.subvalues_by_timestamp ?? [])
          : Array.isArray(legacy?.subvalues_by_timestamp)
            ? (legacy?.subvalues_by_timestamp ?? [])
            : []
      return subvalues.map((item, idx) => ({
        id: `interruption:${idx}`,
        start: item.start_offset,
        end: Math.max(item.end_offset, item.start_offset + 0.5),
        color: "rgba(255, 0, 0, 0.3)",
        drag: false,
        resize: false,
      }))
    } catch (e) {
      log.debug("Failed to parse interruption regions", { error: e })
    }
    return []
  }

  getAudioRegionReviewConfig(): AudioRegionReviewConfig | null {
    return {
      category: CATEGORY,
      scoreType: "per_minute",
      buildReviewedResultJson: ({ editedCount, score, durationSeconds }) => ({
        total_interruptions: editedCount,
        interruptions_per_minute: Number(score.toFixed(2)),
        total_duration:
          durationSeconds != null ? Number(durationSeconds.toFixed(2)) : null,
      }),
    }
  }
}
